import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ROOM_TYPES = ["Single", "Double", "Triple", "Dorm"];
const AC_OPTIONS = ["Yes", "No"];

const emptyForm = (campusCode = "") => ({
  id: null,
  CampusCode: campusCode,
  Block: "",
  Floor: "",
  RoomNumber: "",
  RoomType: "Dorm",
  IsAirConditioned: "No",
  Capacity: "",
  Occupied: 0,
  HostelApplicationFee: "",
  HostelDeposit: "",
  HostelFee: "",
  IsActive: 1,
});

const UpdateHostelRooms = () => {
  const token = localStorage.getItem("auth");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = {
    ...surfaceSx,
    borderLeft: `5px solid ${THEME_ORANGE}`,
    p: 2,
  };
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    backgroundColor: "white",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };
  const tableWrapSx = { ...surfaceSx, mt: 2, overflow: "hidden" };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };
  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const [campusCode, setCampusCode] = useState("");
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    Block: [],
    Floor: [],
    RoomNumber: [],
    RoomType: [],
    IsAirConditioned: [],
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || "";
      setCampusCode(cc);
      setFormData(emptyForm(cc));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, [token]);

  const fetchData = async () => {
    if (!campusCode) return;
    try {
      const res = await axios.get(`${API_URL}/api/hostelRooms`, authHeader);
      setData(
        (res.data || []).map((r) => ({
          ...r,
          id: r.id ?? r.ID,
          Floor: String(r.Floor ?? ""),
          RoomNumber: String(r.RoomNumber ?? ""),
          IsActive: Number(r.IsActive ?? 0),
          Occupied: Number(r.Occupied ?? 0),
        }))
      );
    } catch (e) {
      console.error(e);
      setError("Failed to fetch hostel rooms");
    }
  };

  useEffect(() => {
    if (campusCode) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const uniqueValues = (field) =>
    [...new Set(data.map((r) => String(r[field] ?? "").trim()).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
  const inFilter = (selected, value) =>
    !selected.length || selected.some((x) => norm(x) === norm(value));

  const blockOptions = useMemo(() => uniqueValues("Block"), [data]);
  const floorOptions = useMemo(() => uniqueValues("Floor"), [data]);
  const roomOptions = useMemo(() => uniqueValues("RoomNumber"), [data]);

  const filteredRows = useMemo(
    () =>
      data.filter(
        (r) =>
          inFilter(filters.Block, r.Block) &&
          inFilter(filters.Floor, r.Floor) &&
          inFilter(filters.RoomNumber, r.RoomNumber) &&
          inFilter(filters.RoomType, r.RoomType) &&
          inFilter(filters.IsAirConditioned, r.IsAirConditioned)
      ),
    [data, filters]
  );

  useEffect(() => setPage(0), [filters, rowsPerPage]);
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const openAddForm = () => {
    setFormMode("add");
    setFormData(emptyForm(campusCode));
    setFormOpen(true);
  };

  const openEditForm = (row) => {
    setFormMode("edit");
    setFormData({ ...emptyForm(campusCode), ...row, CampusCode: campusCode });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (!saving) setFormOpen(false);
  };

  const updateFormField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const buildPayload = (row) => ({
    Block: String(row.Block || "").trim(),
    Floor: String(row.Floor || "").trim(),
    RoomNumber: String(row.RoomNumber || "").trim(),
    RoomType: row.RoomType,
    IsAirConditioned: row.IsAirConditioned,
    Capacity: row.Capacity === "" ? null : Number(row.Capacity),
    Occupied: Number(row.Occupied || 0),
    HostelApplicationFee:
      row.HostelApplicationFee === "" ? null : Number(row.HostelApplicationFee),
    HostelDeposit: row.HostelDeposit === "" ? null : Number(row.HostelDeposit),
    HostelFee: row.HostelFee === "" ? null : Number(row.HostelFee),
    IsActive: row.IsActive ? 1 : 0,
  });

  const validateForm = () => {
    if (!formData.Block || !formData.Floor || !formData.RoomNumber) {
      setError("Block, Floor and Room Number are required.");
      return false;
    }
    if (!ROOM_TYPES.includes(formData.RoomType)) {
      setError("Please select a valid room type.");
      return false;
    }
    if (!AC_OPTIONS.includes(formData.IsAirConditioned)) {
      setError("Please select whether the room is air conditioned.");
      return false;
    }
    if (formData.Capacity === "" || Number(formData.Capacity) <= 0) {
      setError("Capacity must be greater than zero.");
      return false;
    }
    if (Number(formData.Occupied || 0) > Number(formData.Capacity)) {
      setError("Occupied count cannot be greater than capacity.");
      return false;
    }
    return true;
  };

  const saveForm = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = buildPayload(formData);
      if (formMode === "add") {
        await axios.post(`${API_URL}/api/hostelRooms`, payload, authHeader);
      } else {
        await axios.put(`${API_URL}/api/hostelRooms/${formData.id}`, payload, authHeader);
      }
      setFormOpen(false);
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to save hostel room");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this hostel room?")) return;
    try {
      await axios.delete(`${API_URL}/api/hostelRooms/${id}`, authHeader);
      setSelectedRows((prev) => prev.filter((x) => x !== id));
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete hostel room");
    }
  };

  const currentPageIds = paginatedRows.map((r) => r.id);
  const selectedOnPage = currentPageIds.filter((id) => selectedRows.includes(id));
  const selectAllCurrentPage = (e) =>
    setSelectedRows((prev) =>
      e.target.checked
        ? [...new Set([...prev, ...currentPageIds])]
        : prev.filter((id) => !currentPageIds.includes(id))
    );
  const selectRow = (id) =>
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const bulkStatusUpdate = async (isActive) => {
    if (!selectedRows.length) return;
    try {
      await Promise.all(
        selectedRows.map(async (id) => {
          const row = data.find((r) => r.id === id);
          if (!row) return;
          await axios.put(
            `${API_URL}/api/hostelRooms/${id}`,
            { ...buildPayload(row), IsActive: isActive ? 1 : 0 },
            authHeader
          );
        })
      );
      setSelectedRows([]);
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      console.error(err);
      setError("Failed to update some hostel rooms");
    }
  };

  const downloadSampleExcel = () => {
    const headers = [
      "CampusCode",
      "Block",
      "Floor",
      "RoomNumber",
      "RoomType",
      "IsAirConditioned",
      "Capacity",
      "Occupied",
      "HostelApplicationFee",
      "HostelDeposit",
      "HostelFee",
      "IsActive",
    ];
    const sampleRows = [
      {
        CampusCode: campusCode,
        Block: "A",
        Floor: "1",
        RoomNumber: "101",
        RoomType: "Double",
        IsAirConditioned: "No",
        Capacity: 2,
        Occupied: 0,
        HostelApplicationFee: 500,
        HostelDeposit: 5000,
        HostelFee: 45000,
        IsActive: 1,
      },
      {
        CampusCode: campusCode,
        Block: "A",
        Floor: "2",
        RoomNumber: "201",
        RoomType: "Single",
        IsAirConditioned: "Yes",
        Capacity: 1,
        Occupied: 0,
        HostelApplicationFee: 500,
        HostelDeposit: 7000,
        HostelFee: 65000,
        IsActive: 1,
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));

    const instructions = [
      ["Field", "Allowed / Notes"],
      ["CampusCode", "Optional in file; authenticated campus is always used by the API."],
      ["Block", "Required."],
      ["Floor", "Required. Example: G, 1, 2, 3."],
      ["RoomNumber", "Required. Unique within Campus + Block + Floor."],
      ["RoomType", "Single / Double / Triple / Dorm"],
      ["IsAirConditioned", "Yes / No"],
      ["Capacity", "Required positive number."],
      ["Occupied", "Optional; defaults to 0."],
      ["HostelApplicationFee", "Optional number."],
      ["HostelDeposit", "Optional number."],
      ["HostelFee", "Optional number."],
      ["IsActive", "0 or 1"],
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HostelRooms");
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 24 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `HostelRooms_Sample_${campusCode || "Campus"}.xlsx`
    );
  };

  const handleBulkClick = () => fileInputRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    try {
      await axios.post(`${API_URL}/api/UploadHostelRooms`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const resetFilters = () =>
    setFilters({
      Block: [],
      Floor: [],
      RoomNumber: [],
      RoomType: [],
      IsAirConditioned: [],
    });

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Hostel Rooms
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: ".85rem" }}>
              Maintain hostel room master by block, floor, room, type and AC
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddForm}
              sx={{
                ...primaryBtnSx,
                backgroundColor: "white",
                color: THEME_ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,.92)" },
              }}
            >
              Add Room
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={!selectedRows.length}
              onClick={() => bulkStatusUpdate(true)}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Bulk Activate
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!selectedRows.length}
              onClick={() => bulkStatusUpdate(false)}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Bulk Deactivate
            </Button>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              startIcon={<UploadIcon />}
              onClick={handleBulkClick}
            >
              Bulk Upload
            </Button>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Paper variant="outlined" sx={sectionSx}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Filters
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,.12)" }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", xl: "repeat(5,1fr)" },
                gap: 1.25,
              }}
            >
              <Autocomplete
                multiple
                options={blockOptions}
                value={filters.Block}
                onChange={(_, v) => setFilters((p) => ({ ...p, Block: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Block(s)" size="small" sx={inputSx} />
                )}
              />
              <Autocomplete
                multiple
                options={floorOptions}
                value={filters.Floor}
                onChange={(_, v) => setFilters((p) => ({ ...p, Floor: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Floor(s)" size="small" sx={inputSx} />
                )}
              />
              <Autocomplete
                multiple
                options={roomOptions}
                value={filters.RoomNumber}
                onChange={(_, v) => setFilters((p) => ({ ...p, RoomNumber: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Room(s)" size="small" sx={inputSx} />
                )}
              />
              <Autocomplete
                multiple
                options={ROOM_TYPES}
                value={filters.RoomType}
                onChange={(_, v) => setFilters((p) => ({ ...p, RoomType: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Type" size="small" sx={inputSx} />
                )}
              />
              <Autocomplete
                multiple
                options={AC_OPTIONS}
                value={filters.IsAirConditioned}
                onChange={(_, v) =>
                  setFilters((p) => ({ ...p, IsAirConditioned: v }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="AC" size="small" sx={inputSx} />
                )}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {filteredRows.length} matching room(s)
              </Typography>
              <Button size="small" onClick={resetFilters} sx={{ textTransform: "none" }}>
                Clear filters
              </Button>
            </Box>
          </Paper>

          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper elevation={0} sx={tableWrapSx}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1450 }}>
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedOnPage.length > 0 &&
                          selectedOnPage.length < currentPageIds.length
                        }
                        checked={
                          currentPageIds.length > 0 &&
                          selectedOnPage.length === currentPageIds.length
                        }
                        onChange={selectAllCurrentPage}
                        sx={{
                          color: "rgba(255,255,255,.7)",
                          "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" },
                        }}
                      />
                    </TableCell>
                    {[
                      "#",
                      "Campus",
                      "Block",
                      "Floor",
                      "Room",
                      "Type",
                      "AC",
                      "Capacity",
                      "Occupied",
                      "App Fee",
                      "Deposit",
                      "Hostel Fee",
                      "Active",
                      "Actions",
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row, idx) => (
                    <TableRow key={row.id} sx={zebraRowSx(idx)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(row.id)}
                          onChange={() => selectRow(row.id)}
                        />
                      </TableCell>
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>{row.CampusCode}</TableCell>
                      <TableCell>{row.Block || "—"}</TableCell>
                      <TableCell>{row.Floor || "—"}</TableCell>
                      <TableCell>{row.RoomNumber || "—"}</TableCell>
                      <TableCell>{row.RoomType || "—"}</TableCell>
                      <TableCell>{row.IsAirConditioned || "—"}</TableCell>
                      <TableCell>{row.Capacity ?? "—"}</TableCell>
                      <TableCell>{row.Occupied ?? 0}</TableCell>
                      <TableCell>{row.HostelApplicationFee ?? "—"}</TableCell>
                      <TableCell>{row.HostelDeposit ?? "—"}</TableCell>
                      <TableCell>{row.HostelFee ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.IsActive ? "Active" : "Inactive"}
                          color={row.IsActive ? "success" : "default"}
                          variant={row.IsActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => openEditForm(row)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => deleteRow(row.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!paginatedRows.length && (
                    <TableRow>
                      <TableCell colSpan={15} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        No hostel rooms match the selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell
                      colSpan={15}
                      sx={{
                        p: 0,
                        borderBottom: 0,
                        backgroundColor: "rgba(25,118,210,.025)",
                      }}
                    >
                      <Button
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={openAddForm}
                        sx={{
                          justifyContent: "center",
                          py: 1.35,
                          borderRadius: 0,
                          textTransform: "none",
                          fontWeight: 900,
                          color: THEME_ORANGE,
                          borderTop: `1px solid ${BORDER}`,
                          backgroundColor: "rgba(240,120,40,.035)",
                          "&:hover": { backgroundColor: "rgba(240,120,40,.085)" },
                        }}
                      >
                        Add Room
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
              labelRowsPerPage="Rows per page"
              showFirstButton
              showLastButton
            />
          </Paper>
        </Box>
      </Paper>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 18px 55px rgba(15,23,42,.22)",
            backgroundColor: "white",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15,23,42,.45)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ ...headerSx, borderRadius: 0, px: 3, py: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>
                {formMode === "add" ? "Add Hostel Room" : "Edit Hostel Room"}
              </Typography>
              <Typography sx={{ fontSize: ".82rem", opacity: 0.9 }}>
                Maintain block, floor, room, type, capacity and hostel fees
              </Typography>
            </Box>
            <IconButton onClick={closeForm} sx={{ color: "white" }} disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: BG,
            p: { xs: 2, sm: 3 },
            pt: { xs: 2.5, sm: 3.5 },
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2.5,
              border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${THEME_ORANGE}`,
              backgroundColor: "white",
              boxShadow: "0 6px 18px rgba(25,118,210,.06)",
            }}
          >
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 0.5 }}>
              Room Details
            </Typography>
            <Typography sx={{ fontSize: ".82rem", color: "text.secondary", mb: 2 }}>
              Room identity is Campus + Block + Floor + Room Number.
            </Typography>
            <Divider sx={{ mb: 2.25, borderColor: "rgba(25,118,210,.10)" }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField label="Campus" value={campusCode} disabled fullWidth size="small" sx={inputSx} />
              <TextField
                required
                label="Block"
                value={formData.Block}
                onChange={(e) => updateFormField("Block", e.target.value)}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <TextField
                required
                label="Floor"
                value={formData.Floor}
                onChange={(e) => updateFormField("Floor", e.target.value)}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <TextField
                required
                label="Room Number"
                value={formData.RoomNumber}
                onChange={(e) => updateFormField("RoomNumber", e.target.value)}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <Autocomplete
                options={ROOM_TYPES}
                value={formData.RoomType || null}
                onChange={(_, v) => updateFormField("RoomType", v || "")}
                renderInput={(params) => (
                  <TextField {...params} required label="Room Type" size="small" sx={inputSx} />
                )}
              />
              <Autocomplete
                options={AC_OPTIONS}
                value={formData.IsAirConditioned || null}
                onChange={(_, v) => updateFormField("IsAirConditioned", v || "")}
                renderInput={(params) => (
                  <TextField {...params} required label="Air Conditioned" size="small" sx={inputSx} />
                )}
              />
              <TextField
                required
                type="number"
                label="Capacity"
                value={formData.Capacity}
                onChange={(e) => updateFormField("Capacity", e.target.value)}
                inputProps={{ min: 1 }}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <TextField
                label="Occupied"
                value={formData.Occupied ?? 0}
                disabled
                fullWidth
                size="small"
                sx={inputSx}
                helperText="Managed by hostel allocation records"
              />
              <TextField
                type="number"
                label="Hostel Application Fee"
                value={formData.HostelApplicationFee ?? ""}
                onChange={(e) => updateFormField("HostelApplicationFee", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <TextField
                type="number"
                label="Hostel Deposit"
                value={formData.HostelDeposit ?? ""}
                onChange={(e) => updateFormField("HostelDeposit", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <TextField
                type="number"
                label="Hostel Fee"
                value={formData.HostelFee ?? ""}
                onChange={(e) => updateFormField("HostelFee", e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
                size="small"
                sx={inputSx}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.IsActive}
                    onChange={(_, checked) => updateFormField("IsActive", checked ? 1 : 0)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: THEME_ORANGE },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: THEME_ORANGE,
                      },
                    }}
                  />
                }
                label={formData.IsActive ? "Active" : "Inactive"}
                sx={{
                  m: 0,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  width: "fit-content",
                  backgroundColor: "rgba(25,118,210,.025)",
                }}
              />
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            backgroundColor: "white",
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={closeForm} disabled={saving} variant="outlined" sx={outlinedBtnSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveForm} disabled={saving} sx={primaryBtnSx}>
            {saving ? "Saving..." : formMode === "add" ? "Add Room" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Operation successful!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpdateHostelRooms;
